import { useLazyQuery } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { LineChart } from '@ovComponents/2-component/lineChart/lineChart';
import { useLineChartTokens } from '@ovComponents/2-component/lineChart/lineChart.tokens';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext, usePermissions } from '@providers/userContextProvider';
import { formatMoneyValue } from '@utils/moneyFormatting';
import type { ChartData, ChartOptions } from 'chart.js';
import dayjs from 'dayjs';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  FETCH_SECURITY_PRICE_HISTORY,
  FETCH_SECURITY_PRICE_HISTORY_NO_ORG,
} from '../queries';
import { BOLD_SX, LABEL_SX, SECONDARY_TEXT_SX, SMALL_SX } from '../styles';
import { formatMoney, valueColor } from '../utils';

type PriceHistoryEntry = {
  date: string;
  priceCents: number;
  orgHistory?: { quantity: number; totalCents: number } | null;
};

type SecurityHistoryPanelProps = {
  financialProductId: string;
  ticker: string;
  securityName: string;
  currency: string;
  onClose: () => void;
};

type TimeRange = '1M' | '3M' | '6M' | '1Y';

const TIME_RANGES: TimeRange[] = ['1M', '3M', '6M', '1Y'];

const getStartDate = (range: TimeRange): string => {
  const days: Record<TimeRange, number> = {
    '1M': 30,
    '3M': 91,
    '6M': 182,
    '1Y': 364,
  };
  return dayjs().subtract(days[range], 'day').format('YYYY-MM-DD');
};

const SecurityHistoryPanel = ({
  financialProductId,
  ticker,
  securityName,
  currency,
  onClose,
}: SecurityHistoryPanelProps) => {
  const { activeOrganization } = useContext(UserContext);
  const { permissions } = usePermissions();
  const hasOrgHistory = permissions.includes(
    'read:financial_product_org_history',
  );
  const { comp, sys } = useThemeTokens(useLineChartTokens());
  const [timeRange, setTimeRange] = useState<TimeRange>('1Y');

  const query = hasOrgHistory
    ? FETCH_SECURITY_PRICE_HISTORY
    : FETCH_SECURITY_PRICE_HISTORY_NO_ORG;
  const [fetchHistory, { data, loading, error }] = useLazyQuery(query, {
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (!financialProductId || !activeOrganization?.id) return;
    fetchHistory({
      variables: {
        input: {
          filter: {
            organizationId: activeOrganization.id,
            financialProductIds: [financialProductId],
            startDate: getStartDate(timeRange),
          },
        },
      },
    });
  }, [financialProductId, activeOrganization?.id, timeRange, fetchHistory]);

  const priceHistory: PriceHistoryEntry[] = useMemo(
    () =>
      [...(data?.fetchFinancialProductPriceHistory?.priceHistory || [])].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      ),
    [data],
  );

  const chartConfig = useMemo(() => {
    if (priceHistory.length < 2) return null;
    const labels = priceHistory.map((p) => dayjs(p.date).format('MMM D, YYYY'));
    const priceData = priceHistory.map((p) => p.priceCents);
    const hasHoldings =
      hasOrgHistory &&
      priceHistory.some((p) => p.orgHistory && p.orgHistory.totalCents != null);
    const holdingsData = hasHoldings
      ? priceHistory.map((p) =>
          p.orgHistory && p.orgHistory.totalCents != null
            ? p.orgHistory.totalCents
            : null,
        )
      : null;
    const priceColor = comp.lineChart.color || sys.color.positive;
    const holdingsColor = sys.color.warning;
    const datasets: ChartData<'line'>['datasets'] = [
      {
        label: `${ticker} Price (${currency})`,
        data: priceData,
        borderColor: priceColor,
        backgroundColor: `${priceColor}14`,
        fill: true,
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        yAxisID: 'y',
      },
      ...(holdingsData
        ? [
            {
              label: 'Holdings Value',
              data: holdingsData,
              borderColor: holdingsColor,
              backgroundColor: `${holdingsColor}0D`,
              fill: true,
              tension: 0.3,
              borderWidth: 1.5,
              borderDash: [6, 3] as number[],
              pointRadius: 0,
              pointHoverRadius: 4,
              yAxisID: 'y1',
              spanGaps: true,
            },
          ]
        : []),
    ];
    const overrideData: ChartData<'line'> = { labels, datasets };
    const overrideOptions: ChartOptions<'line'> = {
      animation: { duration: 300 },
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: 'index' },
      plugins: {
        tooltip: {
          position: 'nearest',
          callbacks: {
            label: (ctx: any) =>
              ctx.datasetIndex === 0
                ? ` Price: $${(ctx.raw / 100).toFixed(2)} ${currency}`
                : ` Holdings: ${formatMoneyValue(ctx.raw, '$', 2)}`,
          },
        },
      },
      scales: {
        x: {
          display: true,
          grid: { display: false },
          border: { display: true },
          ticks: {
            maxTicksLimit: 6,
            autoSkip: true,
            maxRotation: 0,
            align: 'start',
            font: { size: 10 },
          },
        },
        y: {
          position: 'left',
          display: true,
          grid: { display: false },
          border: { display: true },
          title: {
            display: true,
            text: `Price (${currency})`,
            font: { size: 10 },
          },
          ticks: {
            callback: (value: string | number) =>
              `$${(+value / 100).toFixed(2)}`,
            maxTicksLimit: 5,
            font: { size: 10 },
          },
        },
        ...(holdingsData
          ? {
              y1: {
                position: 'right' as const,
                display: true,
                grid: { drawOnChartArea: false },
                border: { display: true },
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Holdings Value',
                  font: { size: 10 },
                },
                ticks: {
                  callback: (value: string | number) =>
                    formatMoney(+value, { compact: true }),
                  maxTicksLimit: 5,
                  font: { size: 10 },
                },
              },
            }
          : {}),
      },
      layout: { padding: { top: 8, bottom: 4, left: 4, right: 4 } },
    };
    return { overrideData, overrideOptions };
  }, [
    priceHistory,
    hasOrgHistory,
    comp.lineChart.color,
    ticker,
    currency,
    sys,
  ]);

  const latestPrice =
    priceHistory.length > 0
      ? priceHistory[priceHistory.length - 1].priceCents
      : null;
  const firstPrice =
    priceHistory.length > 1 ? priceHistory[0].priceCents : null;
  const priceChange =
    latestPrice !== null && firstPrice !== null
      ? ((latestPrice - firstPrice) / firstPrice) * 100
      : null;

  const LEGEND_ITEMS = [
    { label: 'Price', color: comp.lineChart.color },
    { label: 'Holdings Value', color: sys.color.warning, dashed: true },
  ];
  const showLegend =
    chartConfig &&
    hasOrgHistory &&
    priceHistory.some((p) => p.orgHistory?.totalCents);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        bgcolor: sys.color.background,
        borderTop: '1px solid',
        borderColor: sys.color.outlineVariant,
      }}
    >
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={1}
      >
        <Box display='flex' alignItems='baseline' gap={1}>
          <Typography variant='labelSmall' sx={BOLD_SX}>
            {ticker}
          </Typography>
          <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
            {securityName}
          </Typography>
          {latestPrice !== null && (
            <Typography variant='labelSmall' sx={{ fontWeight: 600 }}>
              ${(latestPrice / 100).toFixed(2)} {currency}
            </Typography>
          )}
          {priceChange !== null && (
            <Typography
              variant='labelSmall'
              sx={{
                fontWeight: 600,
                color: valueColor(sys.color as any, priceChange),
              }}
            >
              {priceChange >= 0 ? '+' : ''}
              {priceChange.toFixed(1)}%
            </Typography>
          )}
        </Box>
        <Box display='flex' alignItems='center' gap={0.5}>
          {TIME_RANGES.map((tr) => (
            <Box
              key={tr}
              onClick={() => setTimeRange(tr)}
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: '4px',
                cursor: 'pointer',
                userSelect: 'none',
                bgcolor:
                  timeRange === tr ? sys.color.primaryVariant : 'transparent',
                '&:hover': { bgcolor: sys.color.neutralHover },
              }}
            >
              <Typography
                variant='labelSmall'
                sx={{
                  ...LABEL_SX,
                  fontWeight: timeRange === tr ? 700 : 400,
                  color:
                    timeRange === tr
                      ? sys.color.primary
                      : sys.color.onSurfaceVariant,
                }}
              >
                {tr}
              </Typography>
            </Box>
          ))}
          <IconButton onClick={onClose} size='small' label='Close chart'>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          height={180}
        >
          <CircularProgress size={20} />
        </Box>
      ) : error ? (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          height={80}
        >
          <Typography variant='labelSmall' sx={{ color: sys.color.negative }}>
            Failed to load price history
          </Typography>
        </Box>
      ) : !chartConfig ? (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          height={80}
        >
          <Typography variant='labelSmall' sx={{ color: sys.color.onDisabled }}>
            Not enough price history data
          </Typography>
        </Box>
      ) : (
        <Box sx={{ height: 200 }}>
          <LineChart
            labels={[]}
            showAxis
            overrideData={chartConfig.overrideData}
            overrideOptions={chartConfig.overrideOptions}
            testId={`security-history-${ticker}`}
          />
        </Box>
      )}

      {showLegend && (
        <Box display='flex' gap={2} mt={0.5} justifyContent='center'>
          {LEGEND_ITEMS.map(({ label, color, dashed }) => (
            <Box key={label} display='flex' alignItems='center' gap={0.5}>
              <Box
                sx={{
                  width: 16,
                  height: 2,
                  bgcolor: color,
                  borderRadius: 1,
                  ...(dashed && { borderStyle: 'dashed' }),
                }}
              />
              <Typography
                variant='labelSmall'
                sx={{ ...SECONDARY_TEXT_SX, ...SMALL_SX }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SecurityHistoryPanel;
